import React, { useEffect, useState } from "react";
import { Plus, Trash2, Layers } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    const [g, d] = await Promise.all([api.get("/groups"), api.get("/designs")]);
    setGroups(g.data.groups || []);
    setDesigns(d.data.designs || []);
  };
  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/groups", { name: name.trim() });
      toast.success("Group created");
      setName("");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setGroupDesign = async (gid, designId) => {
    try {
      await api.put(`/groups/${gid}`, { active_design_id: designId || "" });
      toast.success("Pushed to all screens in group");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeGroup = async (gid) => {
    if (!window.confirm("Delete this group? Member screens will keep their current designs.")) return;
    try {
      await api.delete(`/groups/${gid}`);
      toast.success("Group deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Screen groups</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              Multi-location{" "}
              <span style={accentFont} className="text-sky-600">rollouts</span>.
            </h1>
            <p className="mt-3 text-sm text-slate-500 max-w-xl">Group screens by location or zone, then publish a single design to all of them at once.</p>
          </div>
          <Button onClick={() => setCreating(true)} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" data-testid="new-group-button">
            <Plus className="h-4 w-4 mr-1.5" />New group
          </Button>
        </div>

        {groups.length === 0 ? (
          <div className="rounded-3xl p-12 border border-dashed text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-groups">
            <Layers className="h-10 w-10 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={headingFont}>No groups yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">Create your first group to publish designs across multiple TVs in seconds.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="groups-list">
            {groups.map((g) => (
              <div key={g.id} className="rounded-2xl p-5 border backdrop-blur-xl flex flex-wrap items-start gap-4" style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }} data-testid={`group-row-${g.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-semibold text-lg truncate" style={headingFont}>{g.name}</h3>
                  <div className="text-xs text-slate-500 mt-1">{g.screens?.length || 0} screens · {g.screens?.map(s => s.name || "Untitled").join(", ") || "No screens yet"}</div>
                  <p className="text-xs text-slate-400 mt-1">Tip: assign screens to this group from the Screens page.</p>
                </div>
                <div className="w-full md:w-64">
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Push design to group</Label>
                  <Select value={g.active_design_id || ""} onValueChange={(v) => setGroupDesign(g.id, v === "_none" ? "" : v)}>
                    <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`group-design-${g.id}`}>
                      <SelectValue placeholder="Pick a design…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Clear —</SelectItem>
                      {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 hover:bg-rose-50 hover:text-rose-700" onClick={() => removeGroup(g.id)} data-testid={`group-delete-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-2xl bg-white border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-semibold" style={headingFont}>New group</DialogTitle></DialogHeader>
          <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Group name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Downtown locations" className="rounded-xl bg-slate-50 border-slate-200 text-slate-900" data-testid="new-group-name" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" onClick={createGroup} data-testid="new-group-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
